import React from "react";
import "./GlobalPagination.css";

export default function GlobalPagination({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  className = "",
  showFirstLast = true,
  showPrevNext = true,
  maxVisiblePages = 5
}) {
  const getVisiblePages = () => {
    const pages = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();
  const hasPrevEllipsis = visiblePages[0] > 2;
  const hasNextEllipsis = visiblePages[visiblePages.length - 1] < totalPages - 1;

  return (
    <div className={`gp-pagination ${className}`.trim()}>
      <div className="gp-pagination__info">
        Page {currentPage} of {totalPages}
      </div>
      
      <div className="gp-pagination__controls">
        {showFirstLast && (
          <button
            className="gp-pagination__button gp-pagination__button--first"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            aria-label="First page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/>
            </svg>
          </button>
        )}
        
        {showPrevNext && (
          <button
            className="gp-pagination__button gp-pagination__button--prev"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
        )}
        
        {hasPrevEllipsis && (
          <>
            <button
              className="gp-pagination__button"
              onClick={() => onPageChange(1)}
            >
              1
            </button>
            <span className="gp-pagination__ellipsis">...</span>
          </>
        )}
        
        {visiblePages.map((page) => (
          <button
            key={page}
            className={`gp-pagination__button ${
              page === currentPage ? 'gp-pagination__button--active' : ''
            }`}
            onClick={() => onPageChange(page)}
            aria-label={`Page ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        ))}
        
        {hasNextEllipsis && (
          <>
            <span className="gp-pagination__ellipsis">...</span>
            <button
              className="gp-pagination__button"
              onClick={() => onPageChange(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}
        
        {showPrevNext && (
          <button
            className="gp-pagination__button gp-pagination__button--next"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        )}
        
        {showFirstLast && (
          <button
            className="gp-pagination__button gp-pagination__button--last"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            aria-label="Last page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 5l7 7-7 7M6 5l7 7-7 7"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
